/**
 * @file ExtractedInfoPage.tsx
 *
 * @description A React component that displays extracted information from incident reports.
 * This page allows police officers to review, edit, and submit incident information to RMS.
 *
 * @module Views
 * @requires React
 * @requires Material-UI
 */
import {
  Box,
  Button,
  Skeleton,
  alpha,
  useTheme,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PreviewIcon from "@mui/icons-material/Preview";
import { usePageHeader } from "../../context/LayoutContext";
import { ExtractedInformation } from "./ExtractedInformation";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import { createRef, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { VideoData } from "../../shared/interfaces/VideoData";
import { useToast } from "../../shared/toast";
import { Download, Edit as EditIcon } from "@mui/icons-material";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import {
  downloadCombinedTranscriptInLanguage,
  hasAnyTranscript,
} from "@/shared/utils/transcriptDownload";
import { ErrorState, SignatureDialog } from "../../shared/ui";
import { downloadReportPdf } from "../../features/reports/api/downloadReportPdf";
import { DownloadTranscriptLanguageModal } from "@/shared/ui/modals/DownloadTranscriptLanguageModal";
import { VideoPreview } from "../../components/video/VideoPreview";
import { DownloadReportModal } from "@/features/reports/components/DownloadReportModal";
import { ExtractedInformationSkeleton } from "../../components/skeletons/ExtractedInformationSkeleton";
import type { JSONSchema7 } from "json-schema";
import { FieldMapping } from "@/features/reports/config/fieldMapping";
import { getEvidenceData, rmsCommitReport } from "@/features/reports/api/reportApi";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  incidentDataQuery,
  reportMetadataQuery,
} from "../../features/reports/api/reportQueries";
import { currentProfileQuery } from "../../features/users/api/profileQueries";
import { useResolvedReportSchema } from "../../features/reports/hooks/useResolvedReportSchema";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { ReviewHistoryDialog } from "../../components/coReview/ReviewHistoryDialog";
import HistoryIcon from "@mui/icons-material/History";
import { REPORTS_MANAGE_STATION_PERMISSION } from "../../shared/auth/authorization";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";
import { EDITABLE_STATUSES } from "../EditSection/hooks/useReportData";

// Statuses where the officer has already signed and the report has moved past
// the editable draft phase. CHANGES_REQUESTED is intentionally excluded — the
// officer needs to re-sign after addressing CO feedback.
const SIGNED_REPORT_STATUSES: readonly ReportStatus[] = [
  ReportStatus.SIGNED,
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.CO_APPROVED,
  ReportStatus.RMS_COMMITTED,
];

// Statuses the edit page accepts. Auto-redirecting to the edit page for any
// other status would bounce straight back here in a loop, so this reuses the
// edit page's own whitelist.
const EDIT_REDIRECT_STATUSES: readonly ReportStatus[] = EDITABLE_STATUSES;

/**
 * Whether the read-only preview offers the "Edit report" CTA — exactly the
 * predicate the old auto-redirect used. Gating on EDITABLE_STATUSES (not
 * canEdit alone) keeps parity with useReportData's editor->preview bounce:
 * statuses like AWAITING INPUT have canEdit=true but the editor bounces
 * back here, which used to loop.
 */
export function canShowEditCta(args: {
  editable: boolean;
  isReportSigned: boolean;
  isReportCommitted: boolean;
  hasIncident: boolean;
  reportStatus: string;
}): boolean {
  return (
    args.editable &&
    !args.isReportSigned &&
    !args.isReportCommitted &&
    args.hasIncident &&
    EDIT_REDIRECT_STATUSES.includes(args.reportStatus as ReportStatus)
  );
}

// Statuses for which a CO review-history entry exists — anything that has
// entered or completed the CO workflow. Used to gate the "Review history"
// button; still requires the CO feature flag to be enabled.
const REVIEW_HISTORY_VISIBLE_STATUSES: readonly ReportStatus[] = [
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.CO_APPROVED,
  ReportStatus.CHANGES_REQUESTED,
  ReportStatus.RMS_COMMITTED,
];

/**
 * Props for the ExtractedInfoPage component
 * @interface ExtractedInfoPageProps
 * @property {boolean} [isFinalReview] - Indicates if this is the final review stage
 * @property {React.MutableRefObject<EforceIncidentModel | null> | undefined} [incidentModelRef] - Reference to the incident model
 * @property {React.MutableRefObject<{[narrativeID: string]: React.MutableRefObject<boolean>}>} [narrativeEasterEggRefs] - References for tracking easter eggs
 * @property {function} [onEasterEggFound] - Callback when an easter egg is found
 */
interface ExtractedInfoPageProps {
  isFinalReview?: boolean;
  incidentModelRef?:
    | React.MutableRefObject<EforceIncidentModel | null>
    | undefined;
  narrativeEasterEggRefs?: React.MutableRefObject<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>;
  jsonSchema?: JSONSchema7;
  schemaMapping?: Record<string, FieldMapping>;
  onEasterEggFound?: () => void;
  stationId?: string;
}

/**
 * @name ExtractedInfoPage
 *
 * @description Component that displays extracted information from an incident report
 *
 * This component handles:
 * - Fetching and displaying extracted incident information
 * - Editing incident information
 * - Committing reports to RMS
 * - Downloading incident reports
 * - Displaying video previews
 *
 * @param {ExtractedInfoPageProps} props - Component props
 *
 * @returns {JSX.Element} The rendered component
 */
export const ExtractedInfoPage: React.FC<ExtractedInfoPageProps> = ({
  isFinalReview,
  incidentModelRef,
  narrativeEasterEggRefs,
  onEasterEggFound,
  jsonSchema: providedJsonSchema,
  schemaMapping: providedSchemaMapping,
  stationId,
}) => {
  const theme = useTheme();

  // Signer identity for the signature dialog (previously read inside
  // ReportSubmitModal).
  const { data: profileData } = useQuery(currentProfileQuery());
  const userProfile = profileData ?? null;
  // The signature dialog requires typing this exact string — a null profile
  // must not produce "undefined undefined".
  const signerDisplayName =
    [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ") ||
    "Unknown User";

  const permissions = usePermissions();
  const isCo = permissions.includes(REPORTS_MANAGE_STATION_PERMISSION);
  const coApprovalEnabled = useCoApprovalEnabled();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [videoData, setVideoData] = useState<VideoData | null | VideoData[]>(
    null,
  );
  // jsonSchema/schemaMapping: provided by the caller in final-review mode,
  // otherwise resolved by the shared hook below.

  // UI control states
  const [isCommitting, setIsCommitting] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [showDownloadLanguageModal, setShowDownloadLanguageModal] =
    useState(false);
  const [showTranscriptLanguageModal, setShowTranscriptLanguageModal] =
    useState(false);
  const [refsObject, setRefsObject] = useState<
    Record<string, React.RefObject<HTMLDivElement>>
  >({});

  // Hooks for navigation, error handling, etc.
  const toast = useToast();
  const { reportID } = useParams();
  const navigate = useNavigate();

  // Metadata and incident load independently (the old Promise.allSettled).
  const metadataQuery = useQuery({
    ...reportMetadataQuery(reportID ?? ""),
    enabled: !!reportID && !isFinalReview,
  });
  const incidentQuery = useQuery({
    ...incidentDataQuery(reportID ?? ""),
    enabled: !!reportID && !isFinalReview,
  });

  const metadata = metadataQuery.data;
  const editable = metadata?.canEdit ?? false;
  const reportStatus = metadata?.reportStatus ?? "";
  const isReportSigned = SIGNED_REPORT_STATUSES.includes(
    reportStatus as ReportStatus,
  );
  const isReportCommitted = reportStatus === ReportStatus.RMS_COMMITTED;
  const showRMSCommit = metadata ? ![null, "", "KAR"].includes(metadata.rms) : false;
  // Normalize like CoReviewPage and useReportData: null/empty rms means the
  // native KAR schema, so the KAR-only schema slicing below applies.
  const rms = metadata ? metadata.rms || "KAR" : null;

  const incident: EforceIncidentModel | null = isFinalReview
    ? (incidentModelRef?.current ?? null)
    : ((incidentQuery.data?.reportContent as EforceIncidentModel | undefined) ??
      null);

  // The shared pipeline waits for metadata (rms is null until it resolves)
  // and, for KAR, for the incident — so slicing never races either fetch.
  const schemaHook = useResolvedReportSchema({
    enabled: !isFinalReview && !providedJsonSchema && !providedSchemaMapping,
    rms,
    incident,
    karSlice: true,
    refFilter: "local",
  });
  const jsonSchema = providedJsonSchema ?? schemaHook.jsonSchema;
  const schemaMapping = providedSchemaMapping ?? schemaHook.schemaMapping;

  // Show video preview if report is signed and video data exists
  const showVideoPreview = videoData && isReportSigned;

  /**
   * @name createSectionRefs
   *
   * @description Creates references for each section in the incident model
   *
   * @returns {Record<string, React.RefObject<HTMLDivElement>>} Object with section names as keys and refs as values
   */
  const createSectionRefs = useCallback(() => {
    const refs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
    if (incident) {
      Object.entries(incident).forEach(([section, value]) => {
        if (Array.isArray(value)) {
          refs[section] = createRef<HTMLDivElement>();
        }
      });
    }
    return refs;
  }, [incident]);

  /**
   * Creates refs for each section of the incident for scrolling functionality
   *
   * @returns {Record<string, React.RefObject<HTMLDivElement>>} Object containing refs for each section
   */
  useEffect(() => {
    // Create refs for each section of the incident
    if (refsObject && Object.keys(refsObject).length === 0) {
      const refs = createSectionRefs();
      // Only set refsObject if refs contains keys; avoid setting empty objects which would
      // trigger re-renders and cause a maximum update depth exceeded error.
      if (refs && Object.keys(refs).length > 0) {
        setRefsObject(refs);
      }
    }
  }, [incident, createSectionRefs, refsObject]);

  // Fetch evidence data once metadata delivers the evidence ids (non-fatal).
  // Final review renders no video UI — never fan out there, even if cached
  // metadata exposes evidenceIds while the (disabled) query holds data.
  const evidenceIds = metadataQuery.data?.evidenceIds;
  useEffect(() => {
    if (isFinalReview || !evidenceIds) return;
    let cancelled = false;
    Promise.all(evidenceIds.map((id) => getEvidenceData(id)))
      .then((evidenceResults) => {
        if (!cancelled) setVideoData(evidenceResults);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching evidence data:", err);
        toast.error(
          "An error occurred while fetching evidence data. Please try again later.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isFinalReview, evidenceIds, toast]);

  // Set page titles based on current view mode
  const pageTitle = isFinalReview ? "Final Review" : "Generated Report Review";

  const pageSubTitle = isFinalReview
    ? "Review the information once more before submitting"
    : "Review the extracted information from your video evidence";

  // Set page header in app bar (only when not used as child component)
  usePageHeader({
    title: pageTitle,
    subtitle: pageSubTitle,
    icon: <PreviewIcon />,
  });

  // Opens the download language selection modal
  const showDownloadModal = () => {
    setShowDownloadLanguageModal(true);
  };

  // Closes the download language selection modal
  const handleCloseDownloadModal = () => {
    setShowDownloadLanguageModal(false);
  };

  /**
   * @name handleDownloadIncident
   *
   * @description Downloads the incident report in the selected language
   *
   * @param {string} selectedLanguageCode - The language code for the report
   *
   * @returns {Promise<void>} A promise that resolves when the download is complete
   */
  const handleDownloadIncident = (selectedLanguageCode: string) => {
    return downloadReportPdf({
      reportId: reportID ?? "",
      languageCode: selectedLanguageCode,
    })
      .then(() => {
        setShowDownloadLanguageModal(false);
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.error ||
            "An error occurred while downloading the document. Please try again later.",
        );
      });
  };

  // Submits the incident report to the RMS system. Only invocable by COs after approval.
  const handleRMSCommit = async () => {
    if (isCommitting) return;
    setOpenConfirmation(false);
    if (!incident) {
      toast.error("No incident data available.");
      return;
    }
    setIsCommitting(true);
    try {
      const res = await rmsCommitReport(reportID!);
      navigate(
        `/confirmation/${reportID}` +
          (res.eventNumber
            ? `?eventNumber=${encodeURIComponent(res.eventNumber)}`
            : ""),
      );
    } catch (err: any) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          "An error occurred while submitting the report to the RMS servers. Please try again later.",
      );
    } finally {
      setIsCommitting(false);
    }
  };

  // Fatal load failure: show an error state with retry instead of an
  // indefinite skeleton.
  const extractApiError = (error: unknown): string | null =>
    (isAxiosError(error) && error.response?.data?.error) || null;
  const fatalLoadError =
    metadataQuery.error || incidentQuery.error
      ? extractApiError(metadataQuery.error || incidentQuery.error) ||
        "An error occurred while fetching report information. Please try again later."
      : schemaHook.isError
        ? extractApiError(schemaHook.error) ||
          "An error occurred while loading the report form. Please try again."
        : null;
  if (fatalLoadError && (!incident || !jsonSchema || !schemaMapping)) {
    return (
      <ErrorState
        variant="panel"
        message={fatalLoadError}
        onRetry={() => {
          if (metadataQuery.error) void metadataQuery.refetch();
          if (incidentQuery.error) void incidentQuery.refetch();
          // Covers both a failed schema fetch and a failed $ref resolution
          // (the hook re-runs resolution off dataUpdatedAt).
          if (schemaHook.isError) schemaHook.retry();
        }}
      />
    );
  }

  // Renders a loading skeleton when incident data is not yet available
  if (!incident || !jsonSchema || !schemaMapping) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: { xs: 1, sm: 2, md: 3 },
            width: "75vw",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              {/* Button */}
              <Box
                display="flex"
                justifyContent="center"
                width="100%"
                sx={{ my: 2 }}
              >
                <Skeleton width="20%" height={100} />
              </Box>
            </Grid>
          </Grid>
          <ExtractedInformationSkeleton />
        </Box>
      </>
    );
  }

  return (
    <>
      <SignatureDialog
        open={openConfirmation}
        mode="rmsCommit"
        signerName={signerDisplayName}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={handleRMSCommit}
      />
      <DownloadReportModal
        showDownloadLanguageModal={showDownloadLanguageModal}
        handleCloseDownloadModal={handleCloseDownloadModal}
        handleDownloadIncident={handleDownloadIncident}
      />
      <DownloadTranscriptLanguageModal
        open={showTranscriptLanguageModal}
        onClose={() => setShowTranscriptLanguageModal(false)}
        onDownload={async (languageCode) => {
          try {
            await downloadCombinedTranscriptInLanguage(
              Array.isArray(videoData)
                ? videoData
                : videoData
                  ? [videoData]
                  : null,
              `transcript-${reportID ?? "report"}`,
              languageCode,
            );
            setShowTranscriptLanguageModal(false);
          } catch (error) {
            console.error("Failed to download transcript:", error);
            toast.error("Failed to translate the transcript. Please try again.");
          }
        }}
      />
      {/* Action Buttons and Video Preview - only show when not used as child component */}
      {!isFinalReview && (
        <Container maxWidth="xl" sx={{ pt: 2, pb: 1 }}>
          {coApprovalEnabled &&
            REVIEW_HISTORY_VISIBLE_STATUSES.includes(
              reportStatus as ReportStatus,
            ) && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(true)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Review history
              </Button>
            </Box>
          )}
          {/* Edit CTA: the page is a stable read-only view — editable
              reports get an explicit way into the editor instead of the old
              auto-redirect. Same predicate (EDITABLE_STATUSES keeps parity
              with useReportData's reverse bounce — see canShowEditCta). */}
          {canShowEditCta({
            editable,
            isReportSigned,
            isReportCommitted,
            hasIncident: !!incident,
            reportStatus,
          }) && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/edit-section/${reportID}`)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                }}
              >
                Edit report
              </Button>
            </Box>
          )}
          {/* Action Buttons */}
          {!editable && isReportSigned && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={showDownloadModal}
                startIcon={<Download />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  boxShadow: `0 4px 14px ${alpha(
                    theme.palette.primary.main,
                    0.3,
                  )}`,
                  "&:hover": {
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.4,
                    )}`,
                  },
                }}
              >
                Download Document
              </Button>
              {hasAnyTranscript(
                Array.isArray(videoData)
                  ? videoData
                  : videoData
                    ? [videoData]
                    : null,
              ) && (
                <Button
                  variant="outlined"
                  startIcon={<SubtitlesIcon />}
                  onClick={() => setShowTranscriptLanguageModal(true)}
                  sx={{
                    ml: 2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                  }}
                >
                  Download Transcript
                </Button>
              )}
              {!isReportCommitted &&
                showRMSCommit &&
                (coApprovalEnabled
                  ? isCo && reportStatus === ReportStatus.CO_APPROVED
                  : true) && (
                  <Button
                    onClick={() => setOpenConfirmation(true)}
                    variant="contained"
                    disabled={isCommitting || !incident}
                    loading={isCommitting}
                    sx={{
                      ml: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.2,
                      boxShadow: `0 4px 14px ${alpha(
                        theme.palette.primary.main,
                        0.3,
                      )}`,
                      "&:hover": {
                        boxShadow: `0 6px 20px ${alpha(
                          theme.palette.primary.main,
                          0.4,
                        )}`,
                      },
                    }}
                  >
                    COMMIT DATA TO RMS
                  </Button>
                )}
            </Box>
          )}

          {showVideoPreview && (
            <VideoPreview videoData={videoData} isMinimizable={false} />
          )}
        </Container>
      )}

      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ExtractedInformation
              formData={incident}
              jsonSchema={jsonSchema}
              schemaMapping={schemaMapping}
              title="Extracted Information"
              narrativeEasterEggRefs={narrativeEasterEggRefs}
              onEasterEggFound={onEasterEggFound}
              stationId={stationId}
            />
          </Grid>
        </Grid>
      </Container>
      {reportID && coApprovalEnabled && (
        <ReviewHistoryDialog
          open={historyOpen}
          reportId={reportID}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </>
  );
};
